import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "@/components/button/secondary-button";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import type { IFilterItem } from "./filter-section-component";

interface IMobileFiltersBarProps {
  /** Rendered inline next to the search and "Filters" triggers. */
  searchFilter?: IFilterItem;
  /** Everything else, rendered inside the drawer. */
  drawerFilters: IFilterItem[];
  activeFiltersCount: number;
  onClickSearch: () => void;
  onClickClear: () => void;
  /** Column settings / export menus. */
  tableActionButtons?: React.ReactNode;
}

/**
 * Narrow-screen replacement for the desktop filter grid: an always-visible search box
 * with its own submit button, plus a bottom sheet holding the remaining filters. The
 * filter inputs themselves are the same components the desktop grid renders.
 */
const MobileFiltersBar = ({
  searchFilter,
  drawerFilters,
  activeFiltersCount,
  onClickSearch,
  onClickClear,
  tableActionButtons,
}: IMobileFiltersBarProps) => {
  const { t } = useTranslation();
  const { primaryColor, neutralColor } = useGomakeTheme();
  const [isOpen, setIsOpen] = useState(false);

  const closeDrawer = () => setIsOpen(false);

  const applyFilters = () => {
    onClickSearch();
    closeDrawer();
  };

  const clearFilters = () => {
    onClickClear();
    closeDrawer();
  };

  return (
    <>
      <Stack direction="row" alignItems="center" gap="6px" width="100%">
        {searchFilter && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>{searchFilter.component}</Box>
            {/* Searching from the drawer is not discoverable, so the text field
                gets its own submit button. */}
            <IconButton
              onClick={onClickSearch}
              aria-label={t("sales.quote.search")}
              sx={{
                flexShrink: 0,
                width: "var(--theme-input-height, 40px)",
                height: "var(--theme-input-height, 40px)",
                borderRadius: "var(--theme-radius-md, 4px)",
                backgroundColor: primaryColor(500),
                color: "#FFF",
                "&:hover": { backgroundColor: primaryColor(600) },
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        )}
        <Badge
          badgeContent={activeFiltersCount}
          color="primary"
          overlap="rectangular"
        >
          <Button
            onClick={() => setIsOpen(true)}
            startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: "var(--theme-input-height, 40px)",
              minWidth: "auto",
              padding: "0 12px",
              borderRadius: "var(--theme-radius-md, 4px)",
              border: `1px solid ${primaryColor(500)}`,
              color: primaryColor(500),
              background: "var(--theme-bg-primary, #FFF)",
              textTransform: "none",
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": { marginRight: "4px", marginLeft: 0 },
              ...FONT_FAMILY.Lexend(500, 14),
            }}
          >
            {t("settings.filter")}
          </Button>
        </Badge>
      </Stack>

      <Drawer
        anchor="bottom"
        open={isOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            maxHeight: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "var(--theme-bg-primary, #FFF)",
          },
        }}
      >
        <Stack height="100%" minHeight={0}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            padding="12px 16px"
            borderBottom={`1px solid ${neutralColor(200)}`}
          >
            <Typography sx={{ ...FONT_FAMILY.Lexend(500, 16) }}>
              {t("settings.filter")}
            </Typography>
            <Stack direction="row" alignItems="center" gap="4px">
              {tableActionButtons}
              <IconButton
                onClick={closeDrawer}
                aria-label={t("modal.close")}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Stack
            gap="14px"
            padding="16px"
            sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}
          >
            {drawerFilters.map((filter) => (
              <Stack key={filter.id} gap="6px">
                <Typography
                  sx={{
                    ...FONT_FAMILY.Lexend(500, 13),
                    color: neutralColor(700),
                  }}
                >
                  {filter.label}
                </Typography>
                {filter.component}
              </Stack>
            ))}
          </Stack>

          <Stack
            direction="row"
            gap="8px"
            padding="12px 16px"
            borderTop={`1px solid ${neutralColor(200)}`}
            sx={{
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
              "& > *": { flex: 1 },
            }}
          >
            <SecondaryButton
              themeAsPrimary
              variant="contained"
              themeVariant="outlined"
              onClick={applyFilters}
            >
              {t("sales.quote.search")}
            </SecondaryButton>
            <SecondaryButton
              themeAsPrimary
              variant="outlined"
              themeVariant="text"
              onClick={clearFilters}
            >
              {t("sales.quote.clear")}
            </SecondaryButton>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};

export { MobileFiltersBar };
